import {
  getCountryCallingCode,
  isSupportedCountry,
  parsePhoneNumber,
  type Country,
} from "react-phone-number-input";

export type PhoneCountry = Country;

/** Where the phone's country starts when neither the number nor the language say. */
export const DEFAULT_PHONE_COUNTRY: PhoneCountry = "DE";

// The region of the UI language, except for English: the app's English is
// "en-GB" for every reader, so it says nothing about where the business is.
const regionOfLocale = (locale: string | undefined): string | undefined => {
  if (!locale) {
    return undefined;
  }
  try {
    const { language, region } = new Intl.Locale(locale);
    return language === "en" ? undefined : region;
  } catch {
    return undefined;
  }
};

const parseSafe = (value: string, country?: PhoneCountry) => {
  try {
    return parsePhoneNumber(value, country);
  } catch {
    return undefined;
  }
};

/** The country of an international (`+…`) number; undefined for anything else. */
export function getCountryOfNumber(value: string): PhoneCountry | undefined {
  const trimmed = value.trim();
  return trimmed.startsWith("+") ? parseSafe(trimmed)?.country : undefined;
}

/**
 * The country the phone field starts in: that of a stored international number,
 * else the region of the UI language (`de-AT` → AT; not for English), else
 * Germany.
 */
export function getInitialPhoneCountry({
  storedNumber,
  locale,
}: {
  storedNumber: string | null | undefined;
  locale: string | undefined;
}): PhoneCountry {
  const storedCountry = storedNumber
    ? getCountryOfNumber(storedNumber)
    : undefined;
  if (storedCountry) {
    return storedCountry;
  }
  const region = regionOfLocale(locale);
  return region && isSupportedCountry(region)
    ? (region as PhoneCountry)
    : DEFAULT_PHONE_COUNTRY;
}

/** The calling code of a country with its plus, e.g. `+49`. */
export const getDialCode = (country: PhoneCountry): string =>
  `+${getCountryCallingCode(country)}`;

/** The E.164 number of a national number typed in the given country; `""` for no input. */
export function toE164(country: PhoneCountry, nationalNumber: string): string {
  if (!nationalNumber.trim()) {
    return "";
  }
  return parseSafe(nationalNumber, country)?.number ?? "";
}

/**
 * Moves the typed national number to another country: `+49301234567` in AT is
 * `+43301234567`. An empty number stays empty.
 */
export function switchPhoneCountry(
  value: string,
  country: PhoneCountry
): string {
  if (!value.trim()) {
    return "";
  }
  const national = parseSafe(value)?.nationalNumber ?? value;
  return toE164(country, String(national));
}

/**
 * What the national-number input is given: the stored number as E.164, a
 * national one read in `country`. Empty when it cannot be read.
 */
export function getPhoneInputValue(
  value: string,
  country: PhoneCountry
): string {
  if (!value.trim()) {
    return "";
  }
  return parseSafe(value, country)?.number ?? "";
}

/** Whether typed or pasted text starts as an international number: `+…` or `00…`. */
export const isInternationalEntry = (raw: string): boolean =>
  /^\s*(\+|00)/.test(raw);

/**
 * Reads an international entry (`+49 30 92103385`, `0049 30 92103385`) as the
 * number it is, with its own country. `null` while it is still being typed
 * (`+`, `+4`) or when it is not an international entry.
 */
export function parseInternationalEntry(
  raw: string
): { country: PhoneCountry; e164: string } | null {
  if (!isInternationalEntry(raw)) {
    return null;
  }
  const parsed = parseSafe(raw.trim().replace(/^00/, "+"));
  return parsed?.country && parsed.number
    ? { country: parsed.country, e164: parsed.number }
    : null;
}
